import { useSearchParams } from 'next/navigation';
import React, {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

const context = createContext<string | undefined>(undefined);

export const PathProvider: FunctionComponent<PropsWithChildren<{ path?: string }>> = ({
  path,
  children,
}) => {
  return <context.Provider value={path}>{children}</context.Provider>;
};

export const usePath = () => {
  const path = useContext(context);
  if (!path) throw new Error('missing "path" in SSR props');
  return path;
};

export const usePathOrDefault = (defaultValue: string = '') => {
  const path = useContext(context);
  if (!path) return defaultValue;
  return path;
};

/**
 * Since Cloudfront strips away query params, we need to use this hook to get them from the URL in the browser
 * @param key query param key
 * @returns query param value
 */
export function useSearchParam(key: string) {
  const [value, setVal] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const searchParams = useSearchParams();

  useEffect(() => {
    const queryParam =
      searchParams?.get(key) ?? new URLSearchParams(window.location.search).get(key);
    if (typeof queryParam === 'string' || queryParam === null) setVal(queryParam);
    setReady(true);
  }, [searchParams, key]);

  /** Removes the query param from the URL. */
  const clear = useCallback(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has(key)) return;
    url.searchParams.delete(key);
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, [key]);

  return { value, ready, clear };
}
